<template>
  <div id="ways-to-help" class="relative py-20 md:py-32 overflow-hidden">
    <!-- Background with diagonal split -->
    <div class="absolute inset-0">
      <div class="absolute inset-0 bg-logo-color"></div>
      <div
        class="absolute bottom-0 left-0 right-0 h-1/2 bg-white transform -skew-y-3 origin-bottom-left"
      ></div>
    </div>

    <!-- Decorative Elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <!-- Large ring -->
      <div
        class="absolute -top-20 -right-20 w-80 h-80 border-[40px] border-white/10 rounded-full"
      ></div>
      <!-- Small dots -->
      <div
        class="absolute top-1/4 left-10 w-4 h-4 bg-white/20 rounded-full"
      ></div>
      <div
        class="absolute top-1/3 left-1/4 w-2 h-2 bg-white/30 rounded-full"
      ></div>
      <div
        class="absolute bottom-1/3 right-10 w-3 h-3 bg-logo-color/20 rounded-full"
      ></div>
      <!-- Triangle shape -->
      <div
        class="absolute top-20 left-20 w-0 h-0 border-l-[20px] border-l-transparent border-b-[35px] border-b-white/10 border-r-[20px] border-r-transparent"
      ></div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Header Section -->
      <div class="text-center max-w-3xl mx-auto mb-16" data-aos="fade-up">
        <span
          class="inline-block px-4 py-1 bg-white/20 rounded-full text-white text-sm font-medium mb-6"
        >
          Ways To Contribute
        </span>
        <h2
          class="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
        >
          How You Can Help
        </h2>
        <p class="text-lg text-white/80 leading-relaxed">
          Choose how you want to make an impact in the lives of young people
        </p>
      </div>

      <!-- Cards Section - Overlapping Style -->
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
          <!-- Card 1 - Volunteer -->
          <div class="group relative" data-aos="fade-up" data-aos-delay="100">
            <div
              class="relative bg-white rounded-3xl p-8 shadow-2xl transform lg:translate-y-8 hover:-translate-y-2 transition-all duration-500"
            >
              <!-- Icon Circle -->
              <div class="absolute -top-8 left-8">
                <div
                  class="w-16 h-16 bg-gradient-to-br from-logo-color to-logo-color/80 rounded-2xl rotate-12 flex items-center justify-center shadow-lg group-hover:rotate-0 transition-transform duration-500"
                >
                  <i class="fas fa-hands-helping text-2xl text-white"></i>
                </div>
              </div>

              <!-- Content -->
              <div class="pt-8">
                <span
                  class="text-xs font-bold text-logo-color/60 uppercase tracking-wider"
                  >Step 01</span
                >
                <h3 class="text-2xl font-bold text-logo-color mt-2 mb-4">
                  Volunteer With Us
                </h3>
                <p class="text-gray-600 leading-relaxed mb-6">
                  Invest a few hours of your time to make a difference. We'll
                  match you to volunteering roles based on your skills. Work
                  one-to-one or with small groups of young people, on-site or
                  remotely.
                </p>

                <!-- Features -->
                <ul class="space-y-3 mb-8">
                  <li class="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      class="w-5 h-5 bg-logo-color/10 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-logo-color"></i>
                    </span>
                    Flexible scheduling
                  </li>
                  <li class="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      class="w-5 h-5 bg-logo-color/10 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-logo-color"></i>
                    </span>
                    Remote or on-site
                  </li>
                  <li class="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      class="w-5 h-5 bg-logo-color/10 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-logo-color"></i>
                    </span>
                    Skill-based matching
                  </li>
                </ul>

                <a
                  href="mailto:support@youthallynow.org?subject=Volunteer%20Application"
                  class="inline-flex items-center gap-2 text-logo-color font-semibold group/link"
                >
                  <span
                    class="border-b-2 border-transparent group-hover/link:border-logo-color transition-all"
                  >
                    Apply to Volunteer
                  </span>
                  <i
                    class="fas fa-arrow-right transform group-hover/link:translate-x-2 transition-transform"
                  ></i>
                </a>
              </div>
            </div>
          </div>

          <!-- Card 2 - Mentor (Featured) -->
          <div class="group relative" data-aos="fade-up" data-aos-delay="200">
            <div
              class="relative bg-logo-color rounded-3xl p-8 shadow-2xl transform hover:-translate-y-2 transition-all duration-500"
            >
              <!-- Popular Badge -->
              <div class="absolute -top-4 right-8">
                <span
                  class="px-4 py-1 bg-white text-logo-color text-xs font-bold rounded-full shadow-lg"
                >
                  Most Needed
                </span>
              </div>

              <!-- Icon Circle -->
              <div class="absolute -top-8 left-8">
                <div
                  class="w-16 h-16 bg-white rounded-2xl rotate-12 flex items-center justify-center shadow-lg group-hover:rotate-0 transition-transform duration-500"
                >
                  <i class="fas fa-user-graduate text-2xl text-logo-color"></i>
                </div>
              </div>

              <!-- Content -->
              <div class="pt-8">
                <span
                  class="text-xs font-bold text-white/60 uppercase tracking-wider"
                  >Step 02</span
                >
                <h3 class="text-2xl font-bold text-white mt-2 mb-4">
                  Be A Mentor or Coach
                </h3>
                <p class="text-white/80 leading-relaxed mb-6">
                  Inspire a young person to reach new heights in their education
                  or profession. Share your industry expertise face-to-face or
                  remotely. We'll structure mentoring time to suit your
                  availability.
                </p>

                <!-- Features -->
                <ul class="space-y-3 mb-8">
                  <li class="flex items-center gap-3 text-sm text-white/80">
                    <span
                      class="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-white"></i>
                    </span>
                    Share your expertise
                  </li>
                  <li class="flex items-center gap-3 text-sm text-white/80">
                    <span
                      class="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-white"></i>
                    </span>
                    Flexible time commitment
                  </li>
                  <li class="flex items-center gap-3 text-sm text-white/80">
                    <span
                      class="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-white"></i>
                    </span>
                    Virtual or in-person
                  </li>
                </ul>

                <a
                  href="mailto:support@youthallynow.org?subject=Mentor%20Application"
                  class="inline-flex items-center gap-2 text-white font-semibold group/link"
                >
                  <span
                    class="border-b-2 border-transparent group-hover/link:border-white transition-all"
                  >
                    Become a Mentor
                  </span>
                  <i
                    class="fas fa-arrow-right transform group-hover/link:translate-x-2 transition-transform"
                  ></i>
                </a>
              </div>
            </div>
          </div>

          <!-- Card 3 - Donate -->
          <div class="group relative" data-aos="fade-up" data-aos-delay="300">
            <div
              class="relative bg-white rounded-3xl p-8 shadow-2xl transform lg:translate-y-8 hover:-translate-y-2 transition-all duration-500"
            >
              <!-- Icon Circle -->
              <div class="absolute -top-8 left-8">
                <div
                  class="w-16 h-16 bg-gradient-to-br from-logo-color to-logo-color/80 rounded-2xl rotate-12 flex items-center justify-center shadow-lg group-hover:rotate-0 transition-transform duration-500"
                >
                  <i class="fas fa-hand-holding-heart text-2xl text-white"></i>
                </div>
              </div>

              <!-- Content -->
              <div class="pt-8">
                <span
                  class="text-xs font-bold text-logo-color/60 uppercase tracking-wider"
                  >Step 03</span
                >
                <h3 class="text-2xl font-bold text-logo-color mt-2 mb-4">
                  Donate
                </h3>
                <p class="text-gray-600 leading-relaxed mb-6">
                  Our work impacts hundreds of young people, especially young
                  girls. Your donation helps us reach more. You can also donate
                  in-kind: books, computers, or building materials for our
                  School Improvement Program.
                </p>

                <!-- Features -->
                <ul class="space-y-3 mb-8">
                  <li class="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      class="w-5 h-5 bg-logo-color/10 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-logo-color"></i>
                    </span>
                    Monetary donations
                  </li>
                  <li class="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      class="w-5 h-5 bg-logo-color/10 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-logo-color"></i>
                    </span>
                    In-kind contributions
                  </li>
                  <li class="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      class="w-5 h-5 bg-logo-color/10 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-check text-xs text-logo-color"></i>
                    </span>
                    100% goes to programs
                  </li>
                </ul>

                <a
                  href="mailto:support@youthallynow.org?subject=Donation%20Inquiry"
                  class="inline-flex items-center gap-2 text-logo-color font-semibold group/link"
                >
                  <span
                    class="border-b-2 border-transparent group-hover/link:border-logo-color transition-all"
                  >
                    Make a Donation
                  </span>
                  <i
                    class="fas fa-arrow-right transform group-hover/link:translate-x-2 transition-transform"
                  ></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WaysToHelp",
};
</script>

<style scoped></style>
